import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { User, Session, ApplicationRecord, Subscription } from '@/api';
import { Route } from 'vue-router';

@Module({
  name: 'AuthModule',
  namespaced: true,
})
export default class AuthModule extends VuexModule {
  ready: boolean = false;

  user: User | null = null;

  session: Session | null = null;

  loginHooks: Array<
    (loginData: { user: User; session: Session }) => Promise<void>
  > = [];

  logoutHooks: Array<() => Promise<void>> = [];

  loginDialog: boolean = false;

  loginCallback: (() => void) | null = null;

  tab: number = 0;

  subscriptions: Subscription[] = [];
  activeIndoorSubscription: boolean = false;
  activeOutdoorSubscription: boolean = false;

  roles: string[] = ['Admins', 'Sales', 'Employees', 'Customers'];

  get isLoggedIn(): boolean {
    return !!this.ready && !!this.user && !!this.session;
  }

  get canAccess() {
    return (roles: string[]): boolean => {
      if (roles.length === 0) {
        return true;
      }
      const userRoles = this.user ? this.user.roles : [];
      for (const role of roles) {
        if (userRoles.includes(role)) {
          return true;
        }
      }
      return false;
    };
  }

  get isAdmin() {
    if (!this.user) {
      return false;
    }
    return this.user.roles.includes('Admins');
  }

  get isSalesPlus() {
    const roles = ['Admins', 'Sales'];
    return roles.some((e: string) => {
      if (!this.user) {
        return false;
      }

      return this.user.roles.includes(e);
    });
  }

  @Mutation
  setReady() {
    this.ready = true;
  }

  @Mutation
  addLoginHook(
    hook: (loginData: { user: User; session: Session }) => Promise<void>,
  ) {
    this.loginHooks.push(hook);
  }

  @Mutation
  addLogoutHook(hook: () => Promise<void>) {
    this.logoutHooks.push(hook);
  }

  @Mutation
  setUser(user: User | null) {
    this.user = user;
  }

  @Mutation
  setSession(session: Session | null) {
    this.session = session;
  }

  @Mutation
  setLoginCallback(cb: (() => void) | null) {
    this.loginCallback = cb;
  }

  @Mutation
  setLoginDialog(show: boolean) {
    this.loginDialog = show;
  }

  @Mutation
  setPopupTab(tab: number) {
    this.tab = tab;
  }

  @Mutation
  setSubscriptions(subscriptions: Subscription[]) {
    this.subscriptions = subscriptions;
  }

  @Mutation
  setActiveIndoorSubscription(bool: boolean) {
    this.activeIndoorSubscription = bool;
  }

  @Mutation
  setActiveOutdoorSubscription(bool: boolean) {
    this.activeOutdoorSubscription = bool;
  }

  @Action({ rawError: true })
  async login({ email, password }: { email: string; password: string }) {
    try {
      const session = new Session({ email, password });
      await session.save();

      if (session && session.id && session.user.id) {
        ApplicationRecord.jwt = session.token;
        const user = (await User.includes(['customer']).find(session.user.id))
          .data;
        this.setSession(session);
        this.setUser(user);
        await this.onLogin();
      }
    } catch (e) {
      this.setSession(null);
      this.setUser(null);
      throw e;
    }
  }

  @Action({ rawError: true })
  async getCurrentUser() {
    try {
      const session = (await Session.first()).data;

      if (session && session.id && session.user.id) {
        const user = (await User.includes(['customer']).find(session.user.id))
          .data;
        this.setSession(session);
        this.setUser(user);
        await this.onLogin();
      }
    } catch (e) {
      this.setSession(null);
      this.setUser(null);
      throw e;
    }
  }

  @Action({ rawError: true })
  async getUserSubscriptions() {
    if (!this.user) {
      return;
    }

    const subscriptions = (
      await Subscription.includes('price.product')
        .where({ user: this.user.id })
        .all()
    ).data;
    this.setSubscriptions(subscriptions);

    const activeIndoorSub = subscriptions.some(
      (sub: Subscription) =>
        sub.valid === true &&
        sub.canceledAt === null &&
        sub.price.product.metadata.codename === 'unlimited_indoor_parking',
    );
    this.setActiveIndoorSubscription(activeIndoorSub);

    const activeOutdoorSub = subscriptions.some(
      (sub: Subscription) =>
        sub.valid === true &&
        sub.canceledAt === null &&
        sub.price.product.metadata.codename === 'unlimited_outdoor_parking',
    );
    this.setActiveOutdoorSubscription(activeOutdoorSub);
  }

  @Action({ rawError: true })
  async onLogin() {
    const { user, session } = this;
    if (!user || !session) {
      throw new Error('user and session not set');
    }
    // call all other modules that need login data
    await Promise.all(this.loginHooks.map(hook => hook({ user, session })));
  }

  @Action({ rawError: true })
  async onLogout() {
    // call all other modules that need login data
    await Promise.all(this.logoutHooks.map(hook => hook()));
  }

  @Action({ rawError: true })
  async logout() {
    const session = new Session();
    try {
      await session.destroy();
    } finally {
      await this.onLogout();
      ApplicationRecord.jwt = undefined;
      window.location.href = '/';
    }
  }

  @Action({ rawError: true })
  async init() {
    try {
      await this.getCurrentUser();
      // logged in
    } catch {
      // not logged in
    } finally {
      // add redirect on 401 middleware
      ApplicationRecord.middlewareStack.afterFilters.push(response => {
        if (response.status === 401) {
          ApplicationRecord.jwt = undefined;
          const next = encodeURIComponent(window.location.pathname);
          window.location.href = `/login?next=${next}`;
        }
      });

      // set ready
      this.setReady();
    }
  }

  @Action
  openLoginDialog({ cb, tab = 0 }: { cb: (() => void) | null; tab?: number }) {
    this.setPopupTab(tab);
    this.setLoginCallback(cb);
    this.setLoginDialog(true);
  }

  @Action
  closeLoginDialog(success: boolean) {
    if (this.loginCallback && success) {
      this.loginCallback();
    }
    this.setLoginCallback(null);
    this.setLoginDialog(false);
  }

  @Action
  trackPageview({
    gtag,
    route,
    pageTitle,
  }: {
    gtag: any;
    route: Route;
    pageTitle?: string;
  }) {
    if (this.isAdmin) {
      return;
    }

    const decodeEntities = (() => {
      // this prevents any overhead from creating the object each time
      const element = document.createElement('div');

      function decodeHTMLEntities(str?: string) {
        if (!str) {
          return '';
        }

        if (str && typeof str === 'string') {
          // strip script/html tags
          str = str.replace(/<script[^>]*>([\S\s]*?)<\/script>/gim, '');
          str = str.replace(/<\/?\w(?:[^"'>]|"[^"]*"|'[^']*')*>/gim, '');
          element.innerHTML = str;
          str = element.textContent || '';
          element.textContent = '';
        }

        return str;
      }

      return decodeHTMLEntities;
    })();

    const decodedTitle = decodeEntities(pageTitle);

    gtag.pageview({
      page_path: route.path,
      page_title: decodedTitle ? decodedTitle : route.meta.title,
    });
  }

  @Action
  trackEvent({
    gtag,
    eventAction,
    eventCategory,
    eventLabel,
    eventValue,
  }: {
    gtag: any;
    eventAction: string;
    eventCategory?: string;
    eventLabel?: string;
    eventValue?: string;
  }) {
    if (this.isAdmin) {
      return;
    }

    gtag.event(eventAction, {
      event_category: eventCategory,
      event_label: eventLabel,
      value: eventValue,
    });
  }
}
